import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { IconPlus } from '@tabler/icons-react';
import { Button } from 'ui';
import { DONATION_TYPES } from 'common/constants';

import BarChartCard from '../components/dashboard/BarChartCard';
import AreaChartCard from '../components/dashboard/AreaChartCard';
import { useDashboard } from '../store/dashboard/hooks';
import { useReports } from '../store/reports/hooks.js';
import Loader from '../components/loader/Loader';
import AlertMessage from '../components/alerts/AlertMessage';
import {
  formatDonationsAmountSpread,
  formatDonationsByDay,
  formatDonationsByHour,
  getDonationAmountStats,
  getDonationCountStats,
  getEmailReportStats,
  getSubscribedContactsStats,
  getTodayDonationStats,
} from '../core/utils';
import SelectCampaign from '../components/dashboard/SelectCampaign';
import DonationHistory from '../components/dashboard/DonationHistory';
import CustomDonationSpreadXAxisTick from '../components/dashboard/CustomDonationSpreadXAxisTick';
import StatsWidget from '../components/dashboard/StatsWidget';
import { useCampaigns } from '../store/campaigns/hooks';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import RecommendedTasksWidget from '../components/dashboard/RecommendedTasksWidget';
import SubscribedContactsWidget from '../components/dashboard/SubscribedContactsWidget';
import {
  getInvoices as getInvoicesApi,
  getSubscribedContactsCount,
  getSubscribedDSContactsCount,
  getDSContactsProspectedCount,
  getUsage as getUsageApi,
} from '../api';
import { useAccount } from '../store/account/hooks';
import { campaignType, USER_ROLES } from '../core/constants';
import { differenceInMonths, isLastDayOfMonth, parseISO } from 'date-fns';

const Dashboard = () => {
  const {
    loading: dashboardLoading,
    error: dashboardError,
    donations,
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
    reports,
    setError: setDashboardError,
    getDonationsData,
  } = useDashboard();
  const { error: reportsError, setError: setReportsError, getReports } = useReports();
  const { resetCampaignDetails, resetCampaignTemplate, updateCampaignDetails } = useCampaigns();
  const navigate = useNavigate();
  const { account } = useAccount();
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [contactsCount, setContactsCount] = useState({ total: 0, donorspring: 0, dSProspected: 0 });
  const [usage, setUsage] = useState([]);

  const getInvoices = useCallback(async () => {
    if (!account?.subscription || account.userRole !== USER_ROLES.administrator) return;

    try {
      const invoices = await getInvoicesApi();
      const hasOpenInvoices = invoices?.some((invoice) => invoice.status === 'open');
      const hasPastDueInvoices = invoices?.some(
        (invoice) => invoice.status === 'open' && invoice.due_date * 1000 < Date.now(),
      );

      if (hasPastDueInvoices) {
        setInvoiceStatus('past_due');
      } else if (hasOpenInvoices) {
        setInvoiceStatus('open');
      } else {
        setInvoiceStatus('');
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [account]);

  const getContactsCount = useCallback(async () => {
    try {
      const [contactsResponse, dsContactsResponse, dSProspectedResponse] = await Promise.all([
        getSubscribedContactsCount(),
        getSubscribedDSContactsCount(),
        getDSContactsProspectedCount(),
      ]);
      setContactsCount({
        total: contactsResponse.data.count || 0,
        donorspring: dsContactsResponse.data.count || 0,
        dSProspected: dSProspectedResponse.data.count || 0,
      });
    } catch (err) {
      Sentry.captureException(err);
    }
  }, []);

  const getUsage = useCallback(async () => {
    try {
      const usage = await getUsageApi();
      setUsage(usage.data);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, []);

  useEffect(() => {
    getDonationsData();
    getReports();
    getInvoices();
    getContactsCount();
    getUsage();
  }, [getDonationsData, getReports, getInvoices, getContactsCount, getUsage]);

  const toggleError = () => {
    if (dashboardError) setDashboardError(false);
    if (reportsError) setReportsError(false);
  };

  const goToNewCampaign = () => {
    resetCampaignDetails();
    resetCampaignTemplate();
    updateCampaignDetails({ type: campaignType.single });
    navigate('/new-campaign');
  };

  const donationAmountStats = useMemo(
    () => getDonationAmountStats(donations, startDate, endDate, comparisonStartDate, comparisonEndDate),
    [donations, startDate, endDate, comparisonStartDate, comparisonEndDate],
  );

  const todayDonationStats = useMemo(() => getTodayDonationStats(donations), [donations]);

  const donorsAllTimeStats = useMemo(() => {
    const oneTimeDonors = donations
      ? [...new Set(donations.filter((d) => d.type === DONATION_TYPES.oneTime).map((d) => d.donorId))]
      : [];
    const recurringDonors = donations
      ? [...new Set(donations.filter((d) => d.type === DONATION_TYPES.recurring).map((d) => d.donorId))]
      : [];
    const totalRaised = donations ? donations.reduce((sum, d) => sum + (d.netAmount || 0), 0) : 0;
    return {
      title: 'Donors All-time',
      totalAmount: `$${(totalRaised / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      trendDirection: null,
      trendAmount: null,
      trendLabel: 'Total Raised',
      dataSections: [
        { label: 'One-time Donors', value: oneTimeDonors.length },
        { label: 'Recurring Donors', value: recurringDonors.length },
      ],
    };
  }, [donations]);

  const subscribedContactsStats = useMemo(() => {
    return getSubscribedContactsStats(contactsCount, usage, startDate, endDate, comparisonStartDate, comparisonEndDate);
  }, [contactsCount, usage, startDate, endDate, comparisonStartDate, comparisonEndDate]);

  const usageStats = useMemo(() => {
    const monthsDiff = Math.abs(differenceInMonths(new Date(startDate * 1000), new Date(endDate * 1000)));

    return usage.filter((stat) => {
      const statDate = parseISO(stat.date);
      const statTime = parseISO(stat.date).getTime() / 1000;
      const isInRange = statTime >= startDate && statTime <= endDate;
      if (monthsDiff > 3) {
        return isInRange && isLastDayOfMonth(statDate);
      }
      return isInRange;
    });
  }, [usage, startDate, endDate]);

  const donationCountStats = useMemo(
    () => getDonationCountStats(donations, startDate, endDate, comparisonStartDate, comparisonEndDate),
    [donations, startDate, endDate, comparisonStartDate, comparisonEndDate],
  );

  const emailReportStats = useMemo(
    () => getEmailReportStats(reports, startDate, endDate, comparisonStartDate, comparisonEndDate),
    [reports, startDate, endDate, comparisonStartDate, comparisonEndDate],
  );

  const donationsByHour = useMemo(() => formatDonationsByHour(donations), [donations]);

  const donationsAmountSpread = useMemo(() => formatDonationsAmountSpread(donations), [donations]);

  const donationsByDay = useMemo(() => formatDonationsByDay(donations), [donations]);

  const error = dashboardError || reportsError;

  return (
    <>
      <div className="flex min-w-[1110px] max-w-7xl flex-col space-y-6">
        {!!invoiceStatus && (
          <div
            className={`rounded border px-6 py-2.5 text-center text-base ${invoiceStatus === 'past_due' ? 'border-red-200 bg-red-50' : 'border-primary-200 bg-primary-50'}`}
          >
            Your account has unpaid invoices. Please review on the{' '}
            <Link
              to="/subscription"
              className={`font-bold ${invoiceStatus === 'past_due' ? 'text-red-700' : 'text-primary-700'}`}
            >
              Subscription
            </Link>{' '}
            page.
          </div>
        )}
        <div className="flex flex-row justify-between">
          <SelectCampaign />

          <div className="flex items-end space-x-3">
            <DateRangePicker />

            <Button title="New Campaign" color="primary" LeftIcon={IconPlus} onClick={goToNewCampaign} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 grid grid-cols-3 gap-4">
            <StatsWidget
              title={donationAmountStats.title}
              totalAmount={donationAmountStats.totalAmount}
              trendDirection={donationAmountStats.trendDirection}
              trendAmount={donationAmountStats.trendAmount}
              trendLabel={donationAmountStats.trendLabel}
              dataSections={donationAmountStats.dataSections}
            />

            <StatsWidget
              title={todayDonationStats.title}
              totalAmount={todayDonationStats.totalAmount}
              trendDirection={todayDonationStats.trendDirection}
              trendAmount={todayDonationStats.trendAmount}
              trendLabel={todayDonationStats.trendLabel}
              dataSections={todayDonationStats.dataSections}
            />

            <StatsWidget
              title={donationCountStats.title}
              totalAmount={donationCountStats.totalAmount}
              trendDirection={donationCountStats.trendDirection}
              trendAmount={donationCountStats.trendAmount}
              trendLabel={donationCountStats.trendLabel}
              dataSections={donationCountStats.dataSections}
            />

            <StatsWidget
              title={emailReportStats.title}
              totalAmount={emailReportStats.totalAmount}
              trendDirection={emailReportStats.trendDirection}
              trendAmount={emailReportStats.trendAmount}
              trendLabel={emailReportStats.trendLabel}
              dataSections={emailReportStats.dataSections}
            />

            <StatsWidget
              title={subscribedContactsStats.title}
              totalAmount={subscribedContactsStats.totalAmount}
              trendDirection={subscribedContactsStats.trendDirection}
              trendAmount={subscribedContactsStats.trendAmount}
              trendLabel={subscribedContactsStats.trendLabel}
              dataSections={subscribedContactsStats.dataSections}
            />

            <StatsWidget
              title={donorsAllTimeStats.title}
              totalAmount={donorsAllTimeStats.totalAmount}
              trendDirection={donorsAllTimeStats.trendDirection}
              trendAmount={donorsAllTimeStats.trendAmount}
              trendLabel={donorsAllTimeStats.trendLabel}
              dataSections={donorsAllTimeStats.dataSections}
            />
          </div>

          <RecommendedTasksWidget />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <AreaChartCard />
            {dashboardLoading && <Loader />}
          </div>

          <div className="relative">
            <SubscribedContactsWidget data={usageStats} />
            {dashboardLoading && <Loader />}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="relative">
            <BarChartCard
              name="Donation Amount Spread"
              data={donationsAmountSpread}
              xDataKey="amountCategory"
              XAxisTick={CustomDonationSpreadXAxisTick}
            />
            {dashboardLoading && <Loader />}
          </div>
          <div className="relative">
            <BarChartCard
              name="Donations By Hour"
              data={donationsByHour}
              xDataKey="hour"
              XAxisTicks={['4 AM', '8 AM', '12 PM', '4 PM', '8 PM']}
            />
            {dashboardLoading && <Loader />}
          </div>
          <div className="relative">
            <BarChartCard name="Donations By Day" data={donationsByDay} xDataKey="day" interval={0} />
            {dashboardLoading && <Loader />}
          </div>
        </div>
        <DonationHistory />
      </div>

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={toggleError}
        severity="error"
      />
    </>
  );
};

export default Dashboard;
